import { useMemo, useState } from "react";
import styled from "styled-components";
import { Modal } from "@/components/modal/modal";
import { colors } from "styles/color-tokens";

import { TextInput } from "ui";
import { Flex } from "ui/commonStyled/layout";
import { Button, FormSize } from "ui/ecosystems/forms";

interface DeleteUserTypes {
  title: string;
  subTitle?: string | JSX.Element;
  defaultConfirmText?: string;
  confirmButtonText?: string;
  children?: JSX.Element;
  onSubmit: () => void;
  closeModal: () => void;
  closeButtonText?: string;
  dataTestId?: string;
  confirmBeforeAction?: boolean;
  isAlert?: boolean;
}

export const ConfirmWithText = (props: DeleteUserTypes) => {
  const {
    title,
    subTitle,
    children,
    defaultConfirmText = "Delete",
    onSubmit,
    closeModal,
    confirmButtonText,
    closeButtonText = "Close",
    dataTestId,
    confirmBeforeAction = true,
    isAlert = true,
  } = props;

  const [confirmText, setConfirmText] = useState("");

  const isDeleteDisabled = useMemo(() => {
    if (!confirmBeforeAction) {
      return false;
    }

    return (
      !confirmText ||
      confirmText?.trim().toLocaleLowerCase() !== defaultConfirmText
    );
  }, [confirmText, defaultConfirmText, confirmBeforeAction]);

  return (
    <Modal
      data-testid={dataTestId}
      show
      onHide={closeModal}
      animation={false}
      className="modal-md-reasonable"
      contentClassName="gap-0 p-0"
    >
      <HeaderSection>
        <Modal.Header closeButton onHide={closeModal}>
          <ModalTitle>{title}</ModalTitle>
        </Modal.Header>
        {subTitle && <SubHeader>{subTitle}</SubHeader>}
      </HeaderSection>

      <BodySection $hasSubTitle={Boolean(subTitle)}>
        {children}

        {confirmBeforeAction && (
          <TextInputStyled
            data-testid="removeAccountField"
            placeholder={defaultConfirmText}
            value={confirmText}
            onChange={setConfirmText}
          />
        )}
      </BodySection>
      <ModalActions justify="space-between" align="center">
        <ActionButton outline size={FormSize.medium} onClick={closeModal}>
          {closeButtonText}
        </ActionButton>
        <ActionButton
          data-testid="confirmDelete"
          alert={isAlert || undefined}
          primary={!isAlert || undefined}
          size={FormSize.medium}
          className={isAlert ? "danger" : ""}
          onClick={onSubmit}
          disabled={isDeleteDisabled}
        >
          {confirmButtonText ? confirmButtonText : "Confirm"}
        </ActionButton>
      </ModalActions>
    </Modal>
  );
};

const Text = styled.p`
  font-size: 14px;
  line-height: 17px;
  font-weight: 300;
`;

const HeaderSection = styled.div`
  padding: 28px 28px 0;
`;

const BodySection = styled(Modal.Body)<{ $hasSubTitle: boolean }>`
  padding: ${({ $hasSubTitle }) =>
    $hasSubTitle ? "30px 28px 0" : "24px 28px 0"};
`;

const ModalTitle = styled.h2`
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  color: ${colors.black};
  margin: 0;
`;

const ModalActions = styled(Flex)`
  padding: 40px 28px 28px;
`;

const SubHeader = styled(Text)`
  margin-top: 16px;
`;

const ActionButton = styled(Button)`
  min-width: 120px;
  padding: 0 23px;
`;

const TextInputStyled = styled(TextInput)`
  width: 192px;
  margin-top: 21px;
`;
